import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment } from "react";
import PulsatingStatusIndicator from "@hl/base-components/lib/PulsatingStatusIndicator";
import { Menu as MenuIcon } from "@hl/base-components/lib/assets/icons.generated";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { CLEAR_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Anchor, Box, Button, CloseButton, Flex, Group, Menu, Modal, Stack, Text, UnstyledButton, createStyles, } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { ViewElsewhereIcon } from "@hl/shared-features/lib/assets/icons.generated";
import { ampli } from "../../ampli";
import { EXTERNAL_URL, envConfig } from "../../config";
import { FEATURE_FLAGS, useEmbedMode, useFeatureFlags } from "../auth/hooks";
import { Balance } from "../balance/Balance";
import { CreateNftButton } from "../collections/create-flow/CreateNftModal";
import { MarketplaceFeeCampaignNavbar } from "../marketplace/fee-campaigns/MarketplaceFeeCampaignNavbar";
import { NotificationFeedNavbar } from "../notifications/NotificationFeedNavbar";
import { GlobalSearch } from "../search/GlobalSearch";
import { NavbarSearchMobile } from "../search/MobileSearch";
import { BrandAnimated, BrandJustLogo, BrandJustText, BrandStatic, } from "./logo";
const exclusivePaths = ["/why-highlight", "/tools/", "/discover"];
const TextLink = ({ link, label, icon, newTab = true }) => {
    const isExclusive = !!exclusivePaths.find((el) => el === location.pathname);
    const isActive = (isExclusive ? location.href : location.origin) === link;
    const textColor = isActive ? TEXT_COLOR.PRIMARY : TEXT_COLOR.PLACEHOLDER;
    return (_jsx(Button, { component: "a", ...(newTab && {
            target: "_blank",
            rel: "noopener noreferrer",
        }), href: link, color: CLEAR_COLOR, compact: true, size: "sm", p: 0, rightIcon: icon, styles: (theme) => ({
            root: {
                "&:hover": {
                    color: theme.fn.themeColor(TEXT_COLOR.PRIMARY),
                },
                "&:not(hover)": {
                    color: theme.fn.themeColor(textColor),
                },
            },
            icon: {
                width: 14,
                color: theme.fn.themeColor(textColor),
            },
        }), children: label }));
};
const useDesktopStyles = createStyles((theme) => ({
    root: {
        gap: 30,
        [theme.fn.largerThan("md")]: {
            gap: 56,
        },
    },
}));
export const NavbarMenuDesktop = ({ menuItems, hideBrand, }) => {
    const { classes } = useDesktopStyles();
    const { isEmbedMode } = useEmbedMode();
    const showLogoOnly = useMediaQuery(`(max-width: 1200px)`);
    return (_jsxs(Group, { className: classes.root, align: "center", h: "100%", noWrap: true, children: [!isEmbedMode && !hideBrand && (_jsx(Box, { sx: {
                    justifySelf: "center",
                }, children: _jsx(Anchor, { href: envConfig.home.baseUrl, display: "flex", target: isEmbedMode ? "_blank" : "_self", children: showLogoOnly ? _jsx(BrandJustLogo, {}) : _jsx(BrandAnimated, {}) }) })), !isEmbedMode && (_jsx(Group, { spacing: 28, align: "center", noWrap: true, children: menuItems.map((item, index) => {
                    if (!item.subMenu) {
                        return _jsx(TextLink, { ...item }, index);
                    }
                    return (_jsxs(Menu, { trigger: "hover", position: "bottom-start", children: [_jsx(Menu.Target, { children: _jsx(Text, { size: "sm", fw: WEIGHT_BOLD, sx: (theme) => ({
                                        cursor: "pointer",
                                        color: theme.fn.themeColor(TEXT_COLOR.PLACEHOLDER),
                                        "&[data-expanded]": {
                                            color: theme.fn.themeColor(TEXT_COLOR.PRIMARY),
                                        },
                                    }), children: item.label }) }), _jsx(Menu.Dropdown, { style: {
                                    border: 0,
                                }, children: item.subMenu.map((subItem, index) => (_jsx(Fragment, { children: _jsx(Menu.Item, { component: "a", href: subItem.link, target: "_blank", rel: "noopener noreferrer", onClick: () => {
                                            if (!ampli.isLoaded) {
                                                return;
                                            }
                                            ampli.learnItemClick({
                                                itemClicked: JSON.stringify(subItem.label),
                                            });
                                        }, children: subItem.label }) }, index))) })] }, index));
                }) }))] }));
};
const NavbarMenuMobile = ({ children, menuItems, enableSearch }) => {
    const { isMobile } = useMantineMediaQueries();
    const [opened, { open, close }] = useDisclosure(false);
    const { isEmbedMode } = useEmbedMode();
    const showFeeCampaign = useMediaQuery(`(max-width: 520px)`);
    return (_jsxs(_Fragment, { children: [_jsxs(Modal, { opened: opened, onClose: close, centered: true, withCloseButton: false, radius: 0, fullScreen: true, styles: {
                    body: {
                        maxHeight: "100vh",
                    },
                    root: {
                        zIndex: 10002,
                    },
                }, children: [_jsx(Stack, { align: "flex-end", mb: 12, children: _jsx(CloseButton, { onClick: close }) }), !isEmbedMode && (_jsxs(Flex, { align: "center", justify: "space-between", children: [_jsx(Anchor, { href: envConfig.home.baseUrl, children: _jsx(BrandStatic, {}) }), showFeeCampaign && _jsx(MarketplaceFeeCampaignNavbar, {})] })), _jsx(Stack, { spacing: 28, mt: 28, align: "flex-start", children: menuItems.map((item, index) => (_jsx(Fragment, { children: _jsx(Box, { onClick: () => {
                                    if (!ampli.isLoaded) {
                                        return;
                                    }
                                    ampli.learnItemClick({
                                        itemClicked: JSON.stringify(item.label),
                                    });
                                }, children: _jsx(TextLink, { ...item }) }) }, index))) })] }), _jsxs(Group, { spacing: 12, position: "apart", align: "center", w: "100%", children: [!isEmbedMode && (_jsx(Anchor, { href: envConfig.home.baseUrl, display: "flex", children: isMobile ? _jsx(BrandJustLogo, {}) : _jsx(BrandJustText, {}) })), _jsxs(Group, { position: "right", children: [children, enableSearch && _jsx(NavbarSearchMobile, {}), !isEmbedMode && (_jsx(UnstyledButton, { onClick: open, sx: { display: "flex", alignItems: "center" }, children: _jsx(MenuIcon, {}) }))] })] })] }));
};
export const Navbar = ({ children }) => {
    const isMobile = useMediaQuery(`(max-width: 1050px)`);
    const showCreateButtonInHeader = useMediaQuery(`(min-width: 576px)`);
    const showFeeCampaign = useMediaQuery(`(min-width: 520px)`);
    const { isEmbedMode } = useEmbedMode();
    const enableSearch = useFeatureFlags(FEATURE_FLAGS.ENABLE_NAVBAR_SEARCH) && !isEmbedMode;
    // const explore = {
    //   label: "Explore",
    //   link: `${envConfig.home.baseUrl}/explore/trending`,
    //   newTab: false,
    // };
    const live = {
        label: (_jsxs(Group, { spacing: 6, pr: 4, children: [_jsx(Text, { unstyled: true, children: "Live" }), _jsx(PulsatingStatusIndicator, { color: STATUS_COLOR.SUCCESS, size: 6 })] })),
        link: `${envConfig.home.baseUrl}/live`,
        newTab: false,
    };
    const knowledgeBase = {
        label: "Help center & documentation",
        link: EXTERNAL_URL.KNOWLEDGE_BASE,
    };
    const mobileCreate = {
        label: "Create and manage collections",
        link: `${envConfig.tools.baseUrl}/`,
    };
    const whyHighlight = {
        label: "Why Highlight",
        link: `${envConfig.home.baseUrl}/why-highlight`,
    };
    const mainNavItems = [live];
    if (isMobile) {
        return (_jsx(NavbarMenuMobile, { menuItems: [
                ...mainNavItems,
                ...(!showCreateButtonInHeader
                    ? [{ ...mobileCreate, newTab: false }]
                    : []),
                { ...knowledgeBase, icon: _jsx(ViewElsewhereIcon, {}) },
                { ...whyHighlight, icon: _jsx(ViewElsewhereIcon, {}) },
            ], enableSearch: enableSearch, children: _jsxs(Box, { sx: { display: "inline-flex", gap: 4 }, children: [_jsx(NotificationFeedNavbar, {}), showFeeCampaign && _jsx(MarketplaceFeeCampaignNavbar, {}), showCreateButtonInHeader && _jsx(CreateNftButton, { isMobile: true }), children] }) }));
    }
    return (_jsxs(Group, { sx: { flex: 1 }, position: "apart", spacing: 32, children: [_jsx(NavbarMenuDesktop, { menuItems: mainNavItems }), enableSearch && (_jsx(Group, { sx: { flex: 1 }, position: "center", children: _jsx(GlobalSearch, {}) })), _jsxs(Box, { sx: { display: "inline-flex", gap: 8, alignItems: "center" }, children: [_jsx(Balance, {}), _jsx(NotificationFeedNavbar, {}), _jsx(MarketplaceFeeCampaignNavbar, {}), _jsx(CreateNftButton, { isMobile: false }), children] })] }));
};
